'use client';

import { useTranslations } from '@ifixit/i18n/hooks';
import { FaIcon } from '@ifixit/ui/components/icon';
import { Textarea } from '@ifixit/ui/components/textarea';
import { cn } from '@ifixit/ui/lib/utils';
import { useId, useState } from 'react';
import type { FeedbackScore } from './types';
import type { FeedbackStep } from './use-feedback-flow';

interface FeedbackCardProps {
   question: string;
   step: FeedbackStep;
   isSending: boolean;
   hasError: boolean;
   maxTextLength: number;
   onVote: (score: FeedbackScore) => void;
   onSendText: (text: string) => void;
}

export function FeedbackCard({
   question,
   step,
   isSending,
   hasError,
   maxTextLength,
   onVote,
   onSendText,
}: FeedbackCardProps) {
   const t = useTranslations('FeedbackPrompt');

   return (
      <div className="flex flex-col gap-3">
         {step === 'vote' && <VoteStep question={question} isSending={isSending} onVote={onVote} />}
         {step === 'text' && (
            <TextStep isSending={isSending} maxTextLength={maxTextLength} onSend={onSendText} />
         )}
         {step === 'thanks' && <ThanksStep />}
         <p role="alert" className={cn('m-0 text-sm text-red-600', !hasError && 'hidden')}>
            {hasError && t('error')}
         </p>
      </div>
   );
}

function VoteStep({
   question,
   isSending,
   onVote,
}: {
   question: string;
   isSending: boolean;
   onVote: (score: FeedbackScore) => void;
}) {
   const t = useTranslations('FeedbackPrompt');

   return (
      <>
         <StepLabel>{t('heading')}</StepLabel>
         <p className="m-0 pr-6 text-base font-medium leading-snug">{question}</p>
         <div className="flex gap-2">
            <VoteButton
               icon="thumbs-up"
               label={t('good')}
               disabled={isSending}
               onClick={() => onVote(1)}
            />
            <VoteButton
               icon="thumbs-down"
               label={t('notGreat')}
               disabled={isSending}
               onClick={() => onVote(-1)}
            />
         </div>
      </>
   );
}

function VoteButton({
   icon,
   label,
   disabled,
   onClick,
}: {
   icon: 'thumbs-up' | 'thumbs-down';
   label: string;
   disabled: boolean;
   onClick: () => void;
}) {
   return (
      <button
         type="button"
         className="button-outline h-16 flex-1 flex-col gap-1 rounded-xl text-xs font-normal text-gray-700 hover:text-brand-500"
         disabled={disabled}
         onClick={onClick}
      >
         <FaIcon name={icon} className="size-6 fill-current" />
         {label}
      </button>
   );
}

function TextStep({
   isSending,
   maxTextLength,
   onSend,
}: {
   isSending: boolean;
   maxTextLength: number;
   onSend: (text: string) => void;
}) {
   const [text, setText] = useState('');
   const textareaId = useId();
   const t = useTranslations('FeedbackPrompt');
   const trimmed = text.trim();
   const canSend = trimmed.length > 0 && !isSending;

   return (
      <form
         className="flex flex-col gap-3"
         onSubmit={event => {
            event.preventDefault();
            if (canSend) {
               onSend(trimmed);
            }
         }}
      >
         <StepLabel>{t('heading')}</StepLabel>
         <label htmlFor={textareaId} className="m-0 pr-6 text-base font-medium leading-snug">
            {t('whatWentWrong')}
         </label>
         <Textarea
            id={textareaId}
            autoFocus
            value={text}
            maxLength={maxTextLength}
            placeholder={t('placeholder')}
            className="min-h-24 resize-none"
            onChange={event => setText(event.target.value)}
         />
         <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">
               {text.length} / {maxTextLength}
            </span>
            <button type="submit" className="button-primary button-sm" disabled={!canSend}>
               {t('send')}
               <FaIcon name="paper-plane" className="size-3.5 fill-current" />
            </button>
         </div>
      </form>
   );
}

function ThanksStep() {
   const t = useTranslations('FeedbackPrompt');

   return (
      <div aria-live="polite" className="flex flex-col gap-2">
         <span className="flex size-10 items-center justify-center rounded-full bg-green-100 text-green-700">
            <FaIcon name="check" className="size-5 fill-current" />
         </span>
         <p className="m-0 text-base font-medium leading-snug">{t('thanksTitle')}</p>
         <p className="m-0 text-sm text-gray-600">{t('thanksBody')}</p>
      </div>
   );
}

function StepLabel({ children }: { children: string }) {
   return <span className="text-xs text-gray-500">{children}</span>;
}
